import { useEffect, useState } from "react";
import { useHub } from "../../../../core/Global";
import { toastError, toastSuccess } from "../../../../components/base/Toast";
import Icon from "../../../../components/base/Icon";
import Div from "../../../../components/base/Div";
import Indicator from "../../../../components/base/Indicator";
import ToggleSwitch from "../../../../components/form/ToggleSwitch";


export default function WalletSettings() {

    const hub = useHub()
    const [fetching, fetchingSet] = useState<boolean>(true)
    const [fetched, fetchedSet] = useState<boolean>(false)
    // Off until the server says otherwise: a new account starts with automatic charge off.
    const [autoCharge, autoChargeSet] = useState<boolean>(false)
    const [budget, budgetSet] = useState<number>(50)
    const [limit, limitSet] = useState<number>(5)
    // The thresholds on offer come from the hub: $5, $10, $15 or $20 (plus one set before that range).
    const [limits, limitsSet] = useState<number[]>([])
    const [working, workingSet] = useState<boolean>()

    const fetch = () => {
        fetchingSet(true);
        hub.Get("/accounts/profile/budget", res => {
            budgetSet(res.data.amount);
            limitSet(res.data.limit);
            limitsSet(res.data.limits ?? []);
            autoChargeSet(res.data.autoChargeEnabled ?? false);
            fetchingSet(false);
            fetchedSet(true);
        });
    }

    useEffect(() => {
        fetch();
    }, [])

    const save = () => {
        workingSet(true);
        hub.Post("/accounts/profile/budget", {
            amount: budget,
            limit: limit,
            autoChargeEnabled: autoCharge
        }, res => {
            if (res.success) toastSuccess("Wallet settings updated successfully!");
            else toastError(res.message ?? "Something went wrong!");
            workingSet(false);
        });
    }

    return (
        <div className="card mb-5">
            <div className="card-header border-0">
                <div className="card-title m-0">
                    <h3 className="fw-bolder m-0">Wallet Settings</h3>
                </div>
                <div className="card-action">
                    <button className="btn btn-sm btn-primary btn-icon w-150px"
                        disabled={budget < 50 || budget > 300 || working}
                        onClick={save}>
                        {
                            working ?
                                <>
                                    <Icon icon="spinner-third" spin />
                                    <span className="ms-2">Updating...</span>
                                </>
                                :
                                <>Update Settings</>
                        }
                    </button>
                </div>
            </div>
            <div className="card-body border-top p-9">
                <Div visible={fetching} className="my-20">
                    <Indicator />
                </Div>
                <Div visible={fetched}>

                    <div className="d-flex align-items-center bg-light-primary rounded p-5 mb-8">
                        <div className="me-5">
                            <ToggleSwitch name="autoCharge" raw checked={autoCharge}
                                onChange={e => autoChargeSet(e)} />
                        </div>
                        <div>
                            <div className="fw-bold fs-5 text-gray-800">Automatic Charge</div>
                            <div className="fs-7 text-gray-600 mt-1">
                                Keep your wallet topped up automatically so your services never stop.
                            </div>
                        </div>
                    </div>

                    <div className={autoCharge ? "" : "opacity-50 pe-none"}>

                        <div className="mb-2 mt-5 fw-bold">Charge Amount: </div>
                        <div className="mb-1">
                            <select className="form-select form-select-sm mw-100 w-300px" value={budget}
                                disabled={!autoCharge}
                                onChange={e => budgetSet(Number.parseInt(e.target.value))}>
                                <option value={50}>$50</option>
                                <option value={100}>$100</option>
                                <option value={150}>$150</option>
                                <option value={200}>$200</option>
                                <option value={250}>$250</option>
                                <option value={300}>$300</option>
                                <option value={301}>More Than $300</option>
                            </select>
                        </div>
                        <div className="fs-7 text-gray-600 mt-2">
                            <Icon icon="info-circle" type="solid" className="me-2" />
                            The amount that will be added to your wallet on each automatic charge.
                        </div>
                        {
                            budget > 300 &&
                            <div className="mt-2 ms-1 fw-bold text-warning">
                                You need to contact our support team to set a charge amount above $300!
                            </div>
                        }

                        <div className="mb-2 mt-8 fw-bold">Minimum Balance: </div>
                        <div className="mb-1">
                            <select className="form-select form-select-sm mw-100 w-300px" value={limit}
                                disabled={!autoCharge}
                                onChange={e => limitSet(Number.parseInt(e.target.value))}>
                                {
                                    limits.map(n =>
                                        <option key={n} value={n}>${n}</option>
                                    )
                                }
                            </select>
                        </div>
                        <div className="fs-7 text-gray-600 mt-2">
                            <Icon icon="info-circle" type="solid" className="me-2" />
                            We will charge your wallet automatically when the balance reaches this amount.
                        </div>

                    </div>

                </Div>
            </div>
        </div>
    )
}
